import type { JSONContent } from '@tiptap/core'

import mixpanel from '@disco/api/mixpanel'
import { findTimestamps } from '@disco/shared-components/rich-text-editor'
import captureError from '@disco/utils/captureError'
import type {
  ContentCounts,
  DiscussionEventContext,
  DiscussionEventTarget,
  NotificationSettingChange,
  NotificationSettingSource,
} from '@disco/utils/discussion-mixpanel'
import {
  buildDiscussionEventProps,
  buildNotificationSettingEventProps,
  countDiscussionContent as countContent,
  DISCUSSION_EVENTS,
} from '@disco/utils/discussion-mixpanel'

// Callers track inside the try blocks of successful saves, so a throw here
// would surface as a failed save and invite a duplicate retry
const withoutThrowing = <T>(run: () => T, fallback: T): T => {
  try {
    return run()
  } catch (error) {
    captureError(error as Error, { moduleName: 'DiscussionMixpanel' })
    return fallback
  }
}

/** Counts mentions and timestamps in a TipTap JSON tree. */
export const countDiscussionContent = (node: JSONContent): ContentCounts =>
  countContent(node, findTimestamps)

// Timestamps are audio-only, so only tracks report them
export const getDiscussionContentProps = (
  target_type: string,
  body: JSONContent,
  { withRanges = false }: { withRanges?: boolean } = {},
): Partial<ContentCounts> =>
  withoutThrowing<Partial<ContentCounts>>(() => {
    const { mention_count, timestamp_count, timestamp_range_count } =
      countDiscussionContent(body)
    return {
      mention_count,
      ...(target_type === 'track'
        ? {
            timestamp_count,
            ...(withRanges ? { timestamp_range_count } : {}),
          }
        : {}),
    }
  }, {})

export const sendDiscussionEvent = (
  name: string,
  context: DiscussionEventContext,
  props: Record<string, unknown> = {},
): void =>
  withoutThrowing(() => {
    mixpanel.sendPureEvent(name, buildDiscussionEventProps(context, props))
  }, undefined)

export const sendNotificationSettingEvent = (
  source: NotificationSettingSource,
  change: NotificationSettingChange,
  target?: DiscussionEventTarget,
): void =>
  withoutThrowing(() => {
    mixpanel.sendPureEvent(
      DISCUSSION_EVENTS.notificationSettingChanged,
      buildNotificationSettingEventProps(source, change, target),
    )
  }, undefined)
